import React, { Fragment, useState } from "react";
import { connect } from "react-redux";
import Moment from "react-moment";
import customConfig from "../../config";
import { Box } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import AppIconButton from "../Common/Button/IconButton";
import {
  InfoPairLabel,
  InfoPairValue,
  SectionHeading,
  ValueText,
} from "../Common/Typography/AppTypography";

/** Show "-" when a value is missing or blank (product fields only). */
const displayOrDash = (value) => {
  if (value === null || value === undefined) return "-";
  const s = String(value).trim();
  return s === "" ? "-" : s;
};

const WarrantyInfo = ({
  products,
  showProductInfo,
  WarrantyData: { warrantyDetails, warrantyError },
  showHeader = true,
}) => {
  const [selectedProduct, setSelectedProduct] = useState(
    products && products.length > 0 ? products[0] : null
  );
  const [selectedWarranty, setSelectedWarranty] = useState(
    products &&
      products.length > 0 &&
      warrantyDetails &&
      warrantyDetails[products[0].id] &&
      warrantyDetails[products[0].id].warranties
      ? warrantyDetails[products[0].id].warranties
      : null
  );

  const changeTab = (p) => {
    setSelectedProduct(p);
    setSelectedWarranty(warrantyDetails[p.id].warranties);
  };

  const isExpirySoon = (expiryDate) => {
    const day = Math.floor(
      (Date.parse(expiryDate) - new Date().getTime()) / (1000 * 3600 * 24)
    );
    if (day <= 30 && day >= 0) return `EXPIRY SOON: ${day} Days Left`;
    if (day < 0) return `EXPIRED`;
    if (day > 30) return null;
    return null;
  };

  const uniqueBoxSx = { mb: 1.25 };
  return (
    <Fragment>
      {!warrantyError ? (
        <Box>
          {showHeader && (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <SectionHeading component="h3" sx={{ mb: 0 }}>
                View Warranty Info
              </SectionHeading>
            </Box>
          )}
          <Box sx={{ px: 2, pb: 2 }}>
            {showProductInfo && products && products.length > 0 && (
              <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                {products.map((product) => (
                  <AppIconButton
                    key={product.id}
                    variant="ghost"
                    size="medium"
                    tooltip={product?.product_name || ""}
                    onClick={() => changeTab(product)}
                    icon={<img src={product.icon} alt="" style={{ width: 32, height: 32 }} />}
                    sx={{
                      p: 1,
                      border: "2px solid",
                      borderColor: selectedProduct?.id === product.id ? "primary.main" : "divider",
                      borderRadius: 1,
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  />
                ))}
              </Box>
            )}
            <Box>
              {showProductInfo && (
                <Grid2 container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Grid2 size={{ xs: 12, sm: 5 }}>
                    <Box {...uniqueBoxSx}>
                      <InfoPairLabel>Product Category</InfoPairLabel>
                      <InfoPairValue>
                        {displayOrDash(selectedProduct?.product_name)}
                      </InfoPairValue>
                    </Box>
                    <Box {...uniqueBoxSx}>
                      <InfoPairLabel>Modal Name</InfoPairLabel>
                      <InfoPairValue>
                        {displayOrDash(selectedProduct?.model_name)}
                      </InfoPairValue>
                    </Box>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 5 }}>
                    <Box {...uniqueBoxSx}>
                      <InfoPairLabel>Sub Category</InfoPairLabel>
                      <InfoPairValue>
                        {displayOrDash(selectedProduct?.sub_product_name)}
                      </InfoPairValue>
                    </Box>
                    <Box {...uniqueBoxSx}>
                      <InfoPairLabel>Serial Number</InfoPairLabel>
                      <InfoPairValue>
                        {displayOrDash(selectedProduct?.serial_no)}
                      </InfoPairValue>
                    </Box>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 2 }}>
                    {selectedProduct?.image && (
                      <Box component="img" src={selectedProduct.image} alt="" sx={{ maxWidth: "100%", height: "auto" }} />
                    )}
                  </Grid2>
                </Grid2>
              )}
              <Box>
                {selectedWarranty && selectedWarranty.length > 0 ? (
                  selectedWarranty.map((warranty) => (
                    <Box
                      key={warranty?.id}
                      sx={{
                        mb: 2,
                        p: 2,
                        border: "1px solid rgba(0,0,0,0.12)",
                        borderRadius: 1,
                        bgcolor: "rgba(0,0,0,0.02)",
                      }}
                    >
                      <ValueText sx={{ fontWeight: 600, mb: 1.5 }}>
                        Warranty ID : {warranty.id}
                      </ValueText>
                      <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                          <Box {...uniqueBoxSx}>
                            <InfoPairLabel>Warranty Start Date</InfoPairLabel>
                            <InfoPairValue>
                              {warranty.WarrantyStartDate && (
                                <Moment format="MMMM Do, YYYY" date={warranty.WarrantyStartDate} />
                              )}
                            </InfoPairValue>
                          </Box>
                          <Box {...uniqueBoxSx}>
                            <InfoPairLabel>Warranty Expiry Date</InfoPairLabel>
                            <InfoPairValue>
                              {warranty.WarrantyExpiryDate && (
                                <Moment format="MMMM Do, YYYY" date={warranty.WarrantyExpiryDate} />
                              )}
                            </InfoPairValue>
                          </Box>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                          <Box {...uniqueBoxSx}>
                            <InfoPairLabel>Type Of Warranty</InfoPairLabel>
                            <InfoPairValue>{warranty.TypeOfWarranty}</InfoPairValue>
                          </Box>
                          {isExpirySoon(warranty.WarrantyExpiryDate) && (
                            <Box {...uniqueBoxSx}>
                              <Box
                                sx={{
                                  display: "inline-block",
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  bgcolor: "warning.light",
                                  color: "warning.dark",
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                }}
                              >
                                {isExpirySoon(warranty.WarrantyExpiryDate)}
                              </Box>
                            </Box>
                          )}
                        </Grid2>
                      </Grid2>
                    </Box>
                  ))
                ) : (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "rgba(211, 47, 47, 0.08)",
                      borderRadius: 1,
                      border: "1px solid rgba(211, 47, 47, 0.3)",
                    }}
                  >
                    <ValueText color="error">
                      {customConfig.noWarrantyRecordsMsg}
                    </ValueText>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box>
          {showHeader && (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <SectionHeading component="h3" sx={{ mb: 0 }}>
                Warranty Details
              </SectionHeading>
            </Box>
          )}
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                p: 2,
                bgcolor: "rgba(211, 47, 47, 0.08)",
                borderRadius: 1,
                border: "1px solid rgba(211, 47, 47, 0.3)",
              }}
            >
              <ValueText color="error">
                {warrantyError}
              </ValueText>
            </Box>
          </Box>
        </Box>
      )}
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  WarrantyData: state.warranty,
});

export default connect(mapStateToProps)(WarrantyInfo);
